/**
 * Service for fetching scheduled appointments with pagination and search
 */

import { graphqlClient } from '@/services/core/graphqlClient';
import { buildScheduledAppointmentsQuery } from '@/services/resources/scheduledAppointmentsGquery';

export interface ScheduledAppointment {
  appointment_id: number;
  appointment_type: number;
  appointment_date_start_time: string;
  appointment_date_end_time: string;
  patient_name: string;
  user_id: number;
  enrollment_patient_id: number;
  clinic_id: number;
  clinic_name: string;
  clinic_timezone: string;
  user_name: string;
  facility_name: string;
  clinic_facility_id: number | null;
}

export interface PaginatedAppointmentsResult {
  appointments: ScheduledAppointment[];
  totalCount: number;
}

export interface AppointmentSearchFilters {
  patientName?: string;
  appointmentDate?: string;
  clinicName?: string;
  userName?: string;
}

interface ScheduledAppointmentsResponse {
  vw_patient_appointment_details: ScheduledAppointment[];
  vw_patient_appointment_details_aggregate: {
    aggregate: {
      count: number;
    };
  };
}

class ScheduledAppointmentsService {
  /**
   * Fetch virtual appointments (appointment_type = 1) with pagination and optional filters
   */
  async getVirtualAppointments(
    hasuraToken: string,
    userId: number | undefined,
    isAdmin: boolean = false,
    limit: number = 10,
    offset: number = 0,
    filters?: AppointmentSearchFilters
  ): Promise<PaginatedAppointmentsResult> {
    try {
      // Build dynamic query with filters
      const query = buildScheduledAppointmentsQuery(1, userId, isAdmin, filters);
      const variables = { limit, offset };
      const queryName = isAdmin 
        ? 'getScheduledVirtualAppointmentsAllFiltered' 
        : 'getScheduledVirtualAppointmentsFiltered';

      const response = await graphqlClient.query<ScheduledAppointmentsResponse>(
        query,
        variables,
        hasuraToken,
        queryName
      );

      if (!response.success || !response.data) {
        console.error('Failed to fetch virtual appointments:', response.errors);
        return { appointments: [], totalCount: 0 };
      }

      return {
        appointments: response.data.vw_patient_appointment_details || [],
        totalCount: response.data.vw_patient_appointment_details_aggregate?.aggregate?.count || 0,
      };
    } catch (error) {
      console.error('Error fetching virtual appointments:', error);
      return { appointments: [], totalCount: 0 };
    }
  }

  /**
   * Fetch in-person appointments (appointment_type = 2) with pagination and optional filters
   */
  async getInPersonAppointments(
    hasuraToken: string,
    userId: number | undefined,
    isAdmin: boolean = false,
    limit: number = 10,
    offset: number = 0,
    filters?: AppointmentSearchFilters
  ): Promise<PaginatedAppointmentsResult> {
    try {
      // Build dynamic query with filters
      const query = buildScheduledAppointmentsQuery(2, userId, isAdmin, filters);
      const variables = { limit, offset };
      const queryName = isAdmin 
        ? 'getScheduledInPersonAppointmentsAllFiltered' 
        : 'getScheduledInPersonAppointmentsFiltered';

      const response = await graphqlClient.query<ScheduledAppointmentsResponse>(
        query,
        variables,
        hasuraToken,
        queryName
      );

      if (!response.success || !response.data) {
        console.error('Failed to fetch in-person appointments:', response.errors);
        return { appointments: [], totalCount: 0 };
      }

      return {
        appointments: response.data.vw_patient_appointment_details || [],
        totalCount: response.data.vw_patient_appointment_details_aggregate?.aggregate?.count || 0,
      };
    } catch (error) {
      console.error('Error fetching in-person appointments:', error);
      return { appointments: [], totalCount: 0 };
    }
  }
}

export const scheduledAppointmentsService = new ScheduledAppointmentsService();
